import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { activateTheme, LoadingIndicators } from '../src/canvasComponents/index';
import { themes } from '../src/canvasComponents/themes';
// hooks
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
// canvas UI
import Footer from '../src/canvasComponents/Footer';
import Nav from '../src/canvasComponents/Nav';
// pages
import DashboardPage from './pages/Dashboard/dashboard';
import ClaimsPage from './pages/Claim/claim';
import ErrorPage from './pages/Error/error';
import { routesArray } from './FormioComponents/utils';
// sass
import './css/App.scss';
// config
import { GtmId, SONNET_AUTH_ORIGIN } from './config';
// Analytics
import * as FS from './analytics/fullstory';
// redux
import { authenticationDataSelector } from './redux/Authentication/selector';
import { logout, changeLanguage } from './redux/Authentication/actions';
import Authentication from './containers/Authenication';
// components
import ChatBot from './components/Chatbot';
import LeavingTooSoonPopUp from './components/LeavingTooSoonPopUp/LeavingTooSoonPopUp';
import AppD from './components/AppD';
// translation files
import TranslationsEN from './translations/en.json';
import TranslationsFR from './translations/fr.json';
// Loading
import { loadingSelector } from './containers/Loading/LoadingSelector';
// Error handling
import ErrorPopup from './components/ErrorPopup';
import ErrorService from './containers/Error';
import { errorSelector } from './containers/Error/ErrorSelector';
import { clearPolicyDetailsError } from './redux/PolicyDetails/actions';
import { clearClaimsErrors } from './redux/Claims/actions';
import { clearServicesErrors } from './redux/Services/actions';
// utils
import { navLinks, handleNavLinkClick } from './utils/navbar/navLinks';
import { setUserPreferredLang, getUserPreferredLang } from './utils/common/appLanguage/appLanguage';
import { setFive9BusinessHours } from './services/five9Service';
import { getCookie, cookies } from './utils/common/cookies';

function App(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authentication, integrationErrors, loadingInfo } = props;
  const { isLoggedIn, preferredLang, sonnetAddressBookUid, userId } = authentication;
  const [supportID] = useState(FS.setUserData(sonnetAddressBookUid));
  const [newPreferredLang, setNewPreferredLang] = useState(preferredLang);
  const [appLanguage, setAppLanguage] = useState(null);
  const [appTranslations, setAppTranslations] = useState(TranslationsEN);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLeavingSoSoonPopUpOpen, setIsLeavingSoSoonPopUpOpen] = useState(false);
  const [navlink, setNavLink] = useState(null);
  const gtmParams = { id: GtmId };
  const [showFooter, setShowFooter] = useState(null);
  const [showNavBar, setShowNavBar] = useState(null);
  const [isViewNative, setIsViewNative] = useState(null);

  if (!window.sessionStorage.getItem('five9')) {
    setFive9BusinessHours();
  }

  useEffect(() => {
    activateTheme(themes.sonnet);
    document.fonts?.ready?.then(() => { setShowFooter(true); });

    // on page refresh, must kick user back to dashboard becuase claim is in draft status
    if (window.location.href.includes('/claims/#/start-claim')) {
      navigate('/dashboard');
    }
  }, []);

  useEffect(() => {
    // Hide navBar, footer for native app rendering
    // incoming request from native SonnetApp (not webapp/digital)
    if (window?.navigator?.userAgent && window?.navigator?.userAgent.includes('SonnetApp')) {
      setIsViewNative(true);
      setShowFooter(false);
      setShowNavBar(false);
    } else {
      setIsViewNative(false);
      setShowFooter(true);
      setShowNavBar(true);
    }
  }, []);

  useEffect(() => {
      setErrors(ErrorService(integrationErrors));
  }, [integrationErrors]);

  useEffect(() => {
    setLoading(loadingInfo.loading);
  }, [loadingInfo]);

  useEffect(() => {
    setAppLanguage(getUserPreferredLang() === 'fr_CA' ? 'fr' : 'en');
    setAppTranslations(getUserPreferredLang() === 'fr_CA' ? TranslationsFR : TranslationsEN);
    dispatch(changeLanguage(newPreferredLang));
    document.documentElement.setAttribute("lang", getUserPreferredLang() === 'fr_CA' ? 'fr' : 'en');
  }, [newPreferredLang]);

  useEffect(()=>{
    if(userId && userId !== 0 ){
        window.dataLayer.push({
            'event': 'UserId',
            'user_id': userId.toString()
        });

        var userProvCookie = getCookie(cookies.userProvince);
        if(userProvCookie){
          var ketchJurisdiction = 'canada';

          if (userProvCookie === 'QC') {
              ketchJurisdiction = 'law25';
          } else if (userProvCookie === 'RoW') {
              ketchJurisdiction = 'us_states';
          }
          window.dataLayer.push({
              'event': 'customJurisdiction',
              'customJurisdiction': ketchJurisdiction
          });
        }
        window.invokeKetch();
    }
  }, [userId]);

  const preferredLangHandler = () => {
    const currentLanguage = getUserPreferredLang();
    setNewPreferredLang(currentLanguage === 'fr_CA' ? 'en_CA' : 'fr_CA');
    setUserPreferredLang(currentLanguage === 'fr_CA' ? 'en_CA' : 'fr_CA');
    if (window.location.href.includes('/claims/#/')) {
      // Reload page when language changes
      window.location.reload();
    }
  };

  const clearErrors = (state) => {
    switch (state) {
      case 'claims':
        dispatch(clearClaimsErrors());
        break;
      case 'policyDetails':
        dispatch(clearPolicyDetailsError());
        break;
      case 'services':
        dispatch(clearServicesErrors());
        break;
      default:
        console.log('no error state found', state);
        break;
    }
  };

  return (
    <div id="app-en-fr" className={`app ${appLanguage}`}>
      <Authentication>
        <GTMProvider state={gtmParams}>
          <IntlProvider messages={appTranslations} locale={appLanguage}>
            <div className="home-structure">
            {showNavBar && (
              <Nav
                secondary
                dashboard
                loggedIn={isLoggedIn}
                onLogout={() => dispatch(logout())}
                setLanguage={() => preferredLangHandler()}
                onLogoClick={(e) => { handleNavLinkClick(e, `${SONNET_AUTH_ORIGIN}/#/account/dashboard`, (link) => setNavLink(link), (flag) => setIsLeavingSoSoonPopUpOpen(flag)); }}
                onCartClick={(e) => { handleNavLinkClick(e, `${SONNET_AUTH_ORIGIN}/#/shopping_cart`, (link) => setNavLink(link), (flag) => setIsLeavingSoSoonPopUpOpen(flag)); }}
                navLinks={navLinks(appLanguage, (link) => setNavLink(link), (flag) => setIsLeavingSoSoonPopUpOpen(flag))}
              />
            )}
              <div className="body-container">
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                  <Route exact path="/healthCheck" element={<p>Health Check end point!</p>} />
                  <Route exact path="/dashboard" element={<DashboardPage />} />
                  <Route exact path="/claims/" element={<ClaimsPage />} />
                  {routesArray.map((route) => <Route exact path={`/claims/#/${route}`} element={<ClaimsPage />} />)}
                  <Route path="*" element={<ErrorPage />} />
                </Routes>
              </div>
              <ChatBot addressBookUID={sonnetAddressBookUid} lang={appLanguage || preferredLang?.substring(0, 2) || 'en'} />
              <AppD />
              <LeavingTooSoonPopUp
                open={isLeavingSoSoonPopUpOpen}
                isClaimAutoSaved={!window.location.href.includes('/start-claim')}
                closeUserIsLeavingTooSoonPopUp={() => setIsLeavingSoSoonPopUpOpen(!isLeavingSoSoonPopUpOpen)}
                handleOnClickLeave={() => { window.open(navlink, '_self'); }}
              />
              <div className="footer-container">
                {!isViewNative && showFooter && (
                  <Footer
                    supportID={isLoggedIn ? supportID : ''}
                    setLanguage={() => preferredLangHandler()}
                  />
                )}
              </div>
            </div>
            <ErrorPopup
              open={errors.isError}
              save={errors.save}
              type={errors.type}
              closeErrorPopUp={() => clearErrors(errors.state)}
            />
            {loading && (
              <div className="loading-overlay">
                <LoadingIndicators.QuoteLoading loading />
              </div>
            )}
          </IntlProvider>
        </GTMProvider>
      </Authentication>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authentication: authenticationDataSelector(state.authentication),
  integrationErrors: errorSelector(state),
  loadingInfo: loadingSelector(state),
});

export default connect(mapStateToProps)(App);
